import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardBody,
    Flex,
    Image,
    Text,
    Badge,
    Spinner,
    Center,
    useColorModeValue,
    Divider,
    SimpleGrid,
    Icon,
    Link,
    Stack,
    Heading,
    useToast,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosClient from '../../axios-client';
import { FaGamepad, FaCalendar, FaHistory, FaEdit, FaExternalLinkAlt } from 'react-icons/fa';
import { MdCategory } from 'react-icons/md';
import { BiMoviePlay } from 'react-icons/bi';
import { ExternalLinkIcon } from '@chakra-ui/icons';

const GameDetails = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();

    const [gameData, setGameData] = useState(null);
    const [loading, setLoading] = useState(true);

    const bgColor = useColorModeValue('white', 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const labelColor = useColorModeValue('gray.600', 'gray.400');
    const valueColor = useColorModeValue('gray.900', 'white');
    const bgGame = useColorModeValue('gray.50', 'gray.600')
    const bgGameHover = useColorModeValue('gray.100', 'gray.500')

    useEffect(() => {
        if (!location.state?.gameId) {
            toast({
                title: 'Erreur',
                description: 'ID du jeu manquant',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            navigate('/jeux');
            return;
        }

        axiosClient.get(`/games/${location.state.gameId}/`)
            .then((response) => {
                setGameData(response.data);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching game details:", error);
                toast({
                    title: 'Erreur',
                    description: 'Impossible de charger les détails du jeu',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                navigate('/jeux');
            });
    }, [location.state?.gameId]);

    if (loading) {
        return (
            <Center h="50vh" mt={10}>
                <Spinner size="xl" />
            </Center>
        );
    }

    if (!gameData) {
        return null;
    }

    const InfoRow = ({ label, value, icon }) => (
        <Flex align="center" py={2}>
            {icon && <Icon as={icon} mr={3} color="orange.500" boxSize={5} />}
            <Box>
                <Text fontSize="sm" color={labelColor} fontWeight="medium">
                    {label}
                </Text>
                <Text fontSize="md" color={valueColor} mt={1}>
                    {value || 'N/A'}
                </Text>
            </Box>
        </Flex>
    );

    return (
        <Box p={5}>
            {/* Breadcrumb */}
            <Box w="100%" mt={3} mb={5}>
                <Text
                    fontFamily='Plus Jakarta Display'
                    as="cite"
                    fontSize={{ base: "18px", md: "20px", lg: "25px" }}
                    color="gray"
                >
                    <span
                        onClick={() => navigate('/')}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={e => e.target.style.textDecoration = "underline"}
                        onMouseLeave={e => e.target.style.textDecoration = "none"}
                    >
                        {t("Accueil")}
                    </span> / <span
                        onClick={() => navigate('/jeux')}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={e => e.target.style.textDecoration = "underline"}
                        onMouseLeave={e => e.target.style.textDecoration = "none"}
                    >
                        {t("Liste des jeux")}
                    </span> / <span>{t("Détails du jeu")}</span>
                </Text>
            </Box>

            {/* Action Buttons */}
            <Flex justify="space-between" align="center" mb={5} flexWrap="wrap" gap={3}>
                <Heading size="lg" color="orange.500">
                    <Icon as={FaGamepad} mr={2} />
                    {gameData.title}
                </Heading>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={3}>
                    <Button
                        leftIcon={<FaEdit />}
                        colorScheme="yellow"
                        onClick={() => navigate('/modifier-jeu', { state: { gameId: gameData.id } })}
                    >
                        {t("Modifier")}
                    </Button>
                    {/* <Button
                        leftIcon={<FaHistory />}
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => navigate('/historique-jeu', { state: { gameId: gameData.id } })}
                    >
                        {t("Historique")}
                    </Button> */}
                </Stack>
            </Flex>

            {/* Main Content */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                {/* Left Column - Image and Basic Info */}
                <Card bg={bgColor} boxShadow="lg" borderRadius="lg">
                    <CardBody>
                        {gameData.image ? (
                            <Image
                                src={gameData.image}
                                alt={gameData.title}
                                borderRadius="md"
                                width="100%"
                                height="auto"
                                maxH="400px"
                                objectFit="cover"
                                mb={4}
                            />
                        ) : (
                            <Flex
                                height="300px"
                                bg="gray.100"
                                borderRadius="md"
                                align="center"
                                justify="center"
                                mb={4}
                            >
                                <Icon as={FaGamepad} boxSize={20} color="gray.400" />
                            </Flex>
                        )}

                       
                        <Divider my={4} />
                        <Stack spacing={3}>
                            <Flex justify="space-between" align="center">
                                <Text fontSize="sm" color={labelColor} fontWeight="medium">
                                    Statut
                                </Text>
                                <Badge
                                    colorScheme={gameData.status === 'published' ? 'green' : 'gray'}
                                    fontSize="md"
                                    px={3}
                                    py={1}
                                    borderRadius="md"
                                >
                                    {gameData.status === 'published' ? t('Publié') : t('Brouillon')}
                                </Badge>
                            </Flex>

                            {/* <Flex justify="space-between" align="center">
                                <Text fontSize="sm" color={labelColor} fontWeight="medium">
                                    État
                                </Text>
                                <Badge
                                    colorScheme={gameData.etat ? 'green' : 'red'}
                                    fontSize="md"
                                    px={3}
                                    py={1}
                                    borderRadius="md"
                                >
                                    {gameData.etat ? t('Activé') : t('Désactivé')}
                                </Badge>
                            </Flex> */}

                            <Divider />

                            <InfoRow
                                label="Date de création"
                                value={new Date(gameData.creation_date).toLocaleDateString('fr-FR', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                                icon={FaCalendar}
                            />
                        </Stack>
                    </CardBody>
                </Card>

                {/* Right Column - Details */}
                <Card bg={bgColor} boxShadow="lg" borderRadius="lg">
                    <CardBody>
                        <Stack spacing={4}>

                        {/* Description */}
                        <Box>
                            <Text fontSize="lg" fontWeight="bold" mb={2} color="orange.500">
                                Description
                            </Text>
                            <Text color={valueColor} lineHeight="tall">
                                {gameData.description}
                            </Text>
                        </Box>

                            {/* Game URL */}
                            <Box>
                                <Text fontSize="lg" fontWeight="bold" mb={2} color="orange.500">
                                    <Icon as={FaExternalLinkAlt} mr={2} />
                                    Lien du jeu
                                </Text>
                                <Link
                                    href={gameData.game_url}
                                    isExternal
                                    color="cyan.500"
                                    fontWeight="medium"
                                    _hover={{ textDecoration: 'underline' }}
                                >
                                    {gameData.game_url}
                                    <ExternalLinkIcon mx={2} />
                                </Link>
                                {/* <Button
                                    as="a"
                                    href={gameData.game_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    mt={3}
                                    colorScheme="orange"
                                    leftIcon={<FaGamepad />}
                                    width="full"
                                >
                                    Jouer maintenant
                                </Button> */}
                            </Box>

                            <Divider />

                            {/* Categories */}
                            {gameData.categories && gameData.categories.length > 0 && (
                                <Box>
                                    <Text fontSize="lg" fontWeight="bold" mb={3} color="orange.500">
                                        <Icon as={MdCategory} mr={2} />
                                        Catégories
                                    </Text>
                                    <Wrap spacing={2}>
                                        {gameData.categories.map((category, index) => (
                                            <WrapItem key={index}>
                                                <Badge
                                                    colorScheme="orange"
                                                    fontSize="sm"
                                                    px={3}
                                                    py={1}
                                                    borderRadius="md"
                                                >
                                                    {category.name}
                                                </Badge>
                                            </WrapItem>
                                        ))}
                                    </Wrap>
                                </Box>
                            )}

                            {/* Associated Contents */}
                            {gameData.contents && gameData.contents.length > 0 && (
                                <>
                                    <Divider />
                                    <Box>
                                        <Text fontSize="lg" fontWeight="bold" mb={3} color="orange.500">
                                            <Icon as={BiMoviePlay} mr={2} />
                                            Contenus associés
                                        </Text>
                                        <Stack spacing={2}>
                                            {gameData.contents.map((content, index) => (
                                                <Flex
                                                    key={index}
                                                    p={3}
                                                    bg={bgGame}
                                                    borderRadius="md"
                                                    align="center"
                                                    gap={3}
                                                    cursor="pointer"
                                                    _hover={{ bg: bgGameHover }}
                                                    onClick={() => {
                                                        navigate('/detail-film', { state: { movieId: content.id } });
                                                    }}
                                                >
                                                    <Box flex="1">
                                                        <Text fontWeight="medium" color={valueColor}>
                                                            {content.title}
                                                        </Text>
                                                        <Badge colorScheme="cyan" fontSize="xs">
                                                            {content.type === 'movie' ? 'Film' : content.type === 'serie' ? 'Série' : content.type}
                                                        </Badge>
                                                    </Box>
                                                </Flex>
                                            ))}
                                        </Stack>
                                    </Box>
                                </>
                            )}
                        </Stack>
                    </CardBody>
                </Card>
            </SimpleGrid>


        </Box>
    );
};

export default GameDetails;